<template>
  <teleport to="body">
    <transition name="loader-fade">
      <div
        v-if="isLoading"
        id="loading-wrapper"
        class="fixed inset-0 grid place-items-center z-loader"
        :style="backgroundStyle"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="loader-card">
          <!-- 3x3 blocks -->
          <div class="loader-mark" aria-hidden="true">
            <div v-for="i in 9" :key="i" class="loader-block"></div>
          </div>

          <div class="loader-title">{{ t('app.name', 'Profiteia.io') }}</div>
          <div class="loader-subtitle">
            {{ t('app.tagline', 'Energy Intelligence') }}
          </div>

          <div class="loader-progress" aria-hidden="true">
            <span></span>
          </div>

          <div class="loader-hint" :class="{ 'is-visible': showHint }">
            <span>{{ t('loading_hints.hint_prefix', 'Taking too long?') }}&nbsp;</span>
            <a href="#" class="loader-link" @click.prevent="handleRefresh">
              {{ t('loading_hints.hint_link', 'Refresh cache') }}
            </a>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { useDisplayStore } from '@/stores/useDisplayStore';

  const { t } = useI18n();

  const authStore = useAuthStore();
  const displayStore = useDisplayStore();
  const route = useRoute();
  const showHint = ref(false);
  let hintTimeout = null;

  // ponytail: 8s, per visible episode. Every path that opens this splash is a
  // network call with no upper bound of its own (the silent auth restore's
  // /current-user, an explicit user fetch, the post-login router handoff), so
  // the gate could outlive its data indefinitely — the reported ~30s was just
  // however long one hung request took to settle. 8s is deliberately past the
  // 4.5s "taking too long?" hint (the escape hatch is offered before we give
  // up) and past the router's own 3s initial-auth-restore bound, so a merely
  // slow boot is not cut short. Raise it only if a normal boot ever exceeds it.
  const MAX_VISIBLE_MS = 8000;

  const isGateOpen = computed(() => {
    if (displayStore.isFullscreenLoaderForced) {
      return true;
    }

    if (route.meta?.formScreen) {
      return false;
    }

    // Startup auth restore is intentionally silent, but it still blocks the
    // first protected route. Keep the recovered fullscreen loader visible
    // until the initial user state has resolved.
    return authStore.isFetchingUser || (authStore.isRefreshingUser && !authStore.isUserLoaded);
  });

  // The splash is a courtesy, not a correctness gate: the shell behind it is
  // already rendered from the cached user. So when the data it waits on does
  // not arrive, we reveal the app rather than trapping the user behind it.
  const gateExpired = ref(false);
  let expiryTimeout = null;

  const isLoading = computed(() => isGateOpen.value && !gateExpired.value);

  watch(
    isGateOpen,
    (open) => {
      if (expiryTimeout) {
        clearTimeout(expiryTimeout);
        expiryTimeout = null;
      }
      if (!open) {
        // Armed per episode, so a later legitimate splash (the post-login
        // handoff) still gets its own full window.
        gateExpired.value = false;
        return;
      }
      expiryTimeout = setTimeout(() => {
        gateExpired.value = true;
      }, MAX_VISIBLE_MS);
    },
    { immediate: true }
  );

  const backgroundStyle = computed(() => {
    return {
      background: `radial-gradient(
      1200px 800px at 30% 20%,
      rgb(var(--color-brand-primary-hover) / 0.25),
      transparent 55%
    ),
    radial-gradient(
      900px 700px at 80% 70%,
      rgb(var(--color-brand-primary) / 0.1),
      transparent 55%
    ),
    var(--loader-bg, rgb(var(--color-bg-app)))`,
    };
  });

  const handleRefresh = () => {
    window.location.reload();
  };

  onMounted(() => {
    showHint.value = false;
    // Show hint after 4.5 seconds
    hintTimeout = setTimeout(() => {
      showHint.value = true;
    }, 4500);
  });

  onUnmounted(() => {
    showHint.value = false;
    if (hintTimeout) {
      clearTimeout(hintTimeout);
    }
    if (expiryTimeout) {
      clearTimeout(expiryTimeout);
      expiryTimeout = null;
    }
  });
</script>

<style scoped>
  /* ===== Loader theme tokens (light default) ===== */
  :root {
    --loader-bg: rgb(var(--color-bg-app));
    --loader-surface: rgb(var(--color-static-white) / 0.75);
    --loader-border: rgb(var(--color-text-primary) / 0.1);
    --loader-text: rgb(var(--color-text-primary));
    --loader-text-muted: rgb(var(--color-text-primary) / 0.65);

    --loader-accent-1: rgb(var(--color-brand-primary-hover) / 1);
    --loader-accent-2: rgb(var(--color-brand-primary));
    --loader-shadow: 0 20px 60px rgb(var(--color-bg-app) / 0.12);

    --block-size: 20px;
    --block-gap: 3px;
    --block-radius: 2px;
  }

  /* Tailwind dark mode uses .dark on <html> */
  html.dark {
    --loader-bg: rgb(var(--color-bg-subtle));
    --loader-surface: rgb(var(--color-surface-raised) / 0.55);
    --loader-border: rgb(var(--color-static-white) / 0.1);
    --loader-text: rgb(var(--color-static-white) / 0.92);
    --loader-text-muted: rgb(var(--color-static-white) / 0.65);

    --loader-accent-1: rgb(var(--color-brand-primary-hover) / 1);
    --loader-accent-2: rgb(var(--color-brand-primary));
    --loader-shadow: 0 24px 70px rgb(var(--color-static-black) / 0.45);
  }

  /* ===== Card ===== */
  .loader-card {
    width: min(520px, calc(100vw - 40px));
    padding: 28px 28px 22px;
    text-align: center;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial,
      'Apple Color Emoji', 'Segoe UI Emoji';
  }

  /* ===== 3x3 "stats blocks" loader ===== */
  .loader-mark {
    display: grid;
    grid-template-columns: repeat(3, var(--block-size));
    grid-template-rows: repeat(3, var(--block-size));
    gap: var(--block-gap);
    justify-content: center;
    margin: 2px auto 16px;
    width: calc((var(--block-size) * 3) + (var(--block-gap) * 2));
  }

  .loader-block {
    width: var(--block-size);
    height: var(--block-size);
    border-radius: var(--block-radius);
    border: 1px solid var(--loader-border);
    background: linear-gradient(135deg, rgb(var(--color-brand-primary-hover) / 0.25), rgb(var(--color-brand-primary) / 0.18));
    opacity: 0.35;
    transform: scale(0.86);
    animation: blockPulse 1.1s ease-in-out infinite;
    will-change: transform, opacity, filter;
  }

  html.dark .loader-block {
    background: linear-gradient(135deg, rgb(var(--color-brand-primary-hover) / 0.25), rgb(var(--color-brand-primary) / 0.22));
  }

  /* Staggered delays across the 3x3 grid */
  .loader-block:nth-child(1) {
    animation-delay: 0ms;
  }
  .loader-block:nth-child(2) {
    animation-delay: 90ms;
  }
  .loader-block:nth-child(3) {
    animation-delay: 180ms;
  }
  .loader-block:nth-child(4) {
    animation-delay: 90ms;
  }
  .loader-block:nth-child(5) {
    animation-delay: 180ms;
  }
  .loader-block:nth-child(6) {
    animation-delay: 270ms;
  }
  .loader-block:nth-child(7) {
    animation-delay: 180ms;
  }
  .loader-block:nth-child(8) {
    animation-delay: 270ms;
  }
  .loader-block:nth-child(9) {
    animation-delay: 360ms;
  }

  @keyframes blockPulse {
    0% {
      opacity: 0.28;
      transform: scale(0.82);
      filter: saturate(0.9);
    }
    45% {
      opacity: 0.95;
      transform: scale(1.05);
      filter: saturate(1.15);
    }
    100% {
      opacity: 0.28;
      transform: scale(0.82);
      filter: saturate(0.9);
    }
  }

  .loader-title {
    font-size: 18px;
    font-weight: 650;
    letter-spacing: 0.2px;
    color: var(--loader-text);
    margin-top: 2px;
  }

  .loader-subtitle {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.15px;
    color: var(--loader-text-muted);
    margin-top: 6px;
  }

  /* ===== Fake progress bar ===== */
  .loader-progress {
    position: relative;
    height: 10px;
    border-radius: 999px;
    border: 1px solid var(--loader-border);
    overflow: hidden;
    margin: 16px auto 10px;
    width: min(340px, 100%);
    background: rgb(var(--color-static-white) / 0.35);
  }

  html.dark .loader-progress {
    background: rgb(var(--color-static-white) / 0.06);
  }

  .loader-progress > span {
    position: absolute;
    inset: 0;
    width: 40%;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      rgb(var(--color-palette-violet) / 0),
      rgb(var(--color-brand-primary-hover) / 0.1),
      rgb(var(--color-brand-primary) / 0.65),
      rgb(var(--color-brand-primary) / 0)
    );
    animation: loaderSweep 1.2s ease-in-out infinite;
  }

  .loader-hint {
    margin-top: 10px;
    font-size: 12px;
    color: var(--loader-text-muted);
    opacity: 0;
    transform: translateY(6px);
    transition: opacity 250ms ease, transform 250ms ease;
    pointer-events: none;
  }

  .loader-hint.is-visible {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  .loader-link {
    color: var(--loader-text);
    text-underline-offset: 3px;
    text-decoration: underline;
  }

  .loader-link:hover {
    opacity: 0.85;
  }

  @keyframes loaderSweep {
    0% {
      transform: translateX(-60%);
      opacity: 0.35;
    }
    50% {
      opacity: 0.9;
    }
    100% {
      transform: translateX(160%);
      opacity: 0.35;
    }
  }

  /* Fade transition */
  .loader-fade-enter-active,
  .loader-fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .loader-fade-enter-from,
  .loader-fade-leave-to {
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .loader-block,
    .loader-progress > span {
      animation: none !important;
    }
  }
</style>
